import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";

import { combineLatest, Observable } from "rxjs";
import { filter, map, scan } from "rxjs/operators";

import { brugPagineretRessource, PagineretRessourceResult, SubSink } from "@dffedb/rxjs";
import { AppShellScrollService } from "@e-forsyning/common/app-shell";
import { AktuelBrugerObserver, Rolle } from "@e-forsyning/common/bruger";
import { Installation, InstallationService, VaelgInstallationService } from "@e-forsyning/common/installation";

@Component({
    selector: "installationer",
    styleUrls: ["./installationer.component.scss"],
    templateUrl: "./installationer.component.html"
})
export class InstallationerComponent implements OnInit, OnDestroy {
    private readonly subs = new SubSink();
    @ViewChild("bottomOfTable", { static: true, read: ElementRef })
    public bottomOfTable: ElementRef;

    public search = new UntypedFormControl(null);
    public huskeliste = new UntypedFormControl(null);
    public installationer: PagineretRessourceResult<Installation>;
    public vm$: Observable<{ installationer: Installation[]; eof: boolean; isLoading: boolean; fastHuskelisteId: number }>;
    public erFilterSynligt$ = this.aktuelBrugerObserver.valueChanges.pipe(map((bruger) => bruger && !Rolle.erForbruger(bruger.rolle)));

    constructor(
        private readonly aktuelBrugerObserver: AktuelBrugerObserver,
        private readonly installationService: InstallationService,
        private readonly vaelgInstallationService: VaelgInstallationService,
        private readonly scrollService: AppShellScrollService
    ) {}

    public ngOnInit(): void {
        this.initialiserPagineretRessource();
        this.initialiserInfiniteScroll();
        this.initialiserVm();

        // Udfør første load af data
        this.installationer.loadMore();
    }

    public initialiserPagineretRessource(): void {
        this.installationer = brugPagineretRessource((sideNummer) => {
            const sideStoerrelse = 20;
            const skip = sideNummer * sideStoerrelse;
            const take = sideStoerrelse;
            const soegetekst = this.search.value;
            const huskelisteId = this.huskeliste.value;
            return this.installationService.hentInstallationer({ skip, take, soegetekst, huskelisteId });
        });
    }

    public initialiserInfiniteScroll(): void {
        const BOTTOM_LOAD_OFFSET = 200;
        const loadMoreNeeded$ = this.scrollService.select().pipe(
            map((event) => ({
                windowPos: event.scrollBottom,
                prevWindowPos: 0,
                loadPos: this.bottomOfTable.nativeElement.offsetTop - BOTTOM_LOAD_OFFSET
            })),
            scan((acc, value) => ({ ...value, prevWindowPos: acc.windowPos })),
            filter(({ windowPos, prevWindowPos, loadPos }) => windowPos >= loadPos && prevWindowPos < loadPos)
        );

        this.subs.sink = loadMoreNeeded$.subscribe((event) => {
            this.installationer.loadMore();
        });
    }

    public initialiserVm(): void {
        const bruger$ = this.aktuelBrugerObserver.valueChanges;
        const installationer$ = this.installationer.result$;
        this.vm$ = combineLatest([bruger$, installationer$]).pipe(
            filter(([_, result]) => !result.isLoadingFirstPage),
            map(([bruger, result]) => ({
                fastHuskelisteId: bruger.huskelisteId,
                installationer: result.items,
                eof: result.eof,
                isLoading: result.isLoading
            }))
        );
    }

    public onHuskelisteChange(): void {
        this.search.setValue(null);
        this.installationer.reset(true);
    }

    public onSoegetekstChange(): void {
        this.huskeliste.setValue(null);
        this.installationer.reset(true);
    }

    public onSelectInstallation(installation: Installation): void {
        this.vaelgInstallationService.vaelgInstallation(installation);
    }

    public loadMore(): void {
        this.installationer.loadMore();
    }

    public ngOnDestroy(): void {
        this.subs.unsubscribe();
    }
}
